import React from 'react'
import { ActionType, ActionIcon, Icon } from '@awell-health/design-system'
import { Sendgrid } from '@awell/ui-kit/src/components/Icons'
import { ActivityObjectType, type Activity } from '../../types'
import { isHITL } from '../FeedItems/ActivityCard/activityCardHelpers'

export const createActionIcon = (activity: Activity, size?: 'sm' | 'md'): React.ReactElement => {
  const { object, indirect_object, icon_url } = activity
  size = size ?? 'sm'

  const iconClass =
    size === 'sm'
      ? 'w-9 h-9 flex bg-slate-100 rounded-md border border-slate-200 items-center justify-center'
      : 'p-2 bg-slate-100 border border-gray-200 rounded-md flex-shrink-0'
  const iconSize = size === 'sm' ? 'h-6 w-6' : ''

  switch (object.type) {
    case ActivityObjectType.Message:
    case ActivityObjectType.Checklist:
    case ActivityObjectType.Calculation:
    case ActivityObjectType.ClinicalNote:
      return <ActionIcon actionType={object.type as unknown as ActionType} size={size === 'sm' ? 'lg' : 'xl'} />
    case ActivityObjectType.Form:
      if (isHITL(activity)) {
        return (
          <div className={iconClass}>
            <Icon icon="RiShieldUserLine" className="h-6 w-6 fill-green-600" />
          </div>
        )
      }
      return <ActionIcon actionType={ActionType.Form} size={size === 'sm' ? 'lg' : 'xl'} />
    case ActivityObjectType.PluginAction:
      if (indirect_object?.name?.toLocaleLowerCase() === 'sendgrid') {
        return (
          <div className={iconClass}>
            <Sendgrid className={`${iconSize} fill-slate-600`} />
          </div>
        )
      }
      return (
        <ActionIcon
          actionType={ActionType.Plugin}
          icon_url={icon_url ?? undefined}
          size={size === 'sm' ? 'lg' : 'xl'}
        />
      )
    case ActivityObjectType.EmrReport:
      return <ActionIcon actionType={ActionType.PushToEmr} size={size === 'sm' ? 'lg' : 'xl'} />
    case ActivityObjectType.Timer:
      return (
        <div className={iconClass}>
          <Icon icon="RiTimeLine" className={`${iconSize} fill-slate-600`} />
        </div>
      )
    case ActivityObjectType.ApiCall:
      return <ActionIcon actionType={ActionType.ApiCall} size={size === 'sm' ? 'lg' : 'xl'} />
    case ActivityObjectType.Agent:
      return (
        <div className={iconClass}>
          <Icon icon="RiSparklingFill" className={`${iconSize} fill-yellow-400`} />
        </div>
      )
    default:
      return <ActionIcon size="lg" />
  }
}

export const createActivityIcon = (activity: Activity): React.ReactElement => {
  const { object, indirect_object, icon_url } = activity

  const iconClass =
    'min-w-[36px] min-h-[36px] w-[36px] h-[36px] bg-slate-100 rounded-md border border-slate-200 flex items-center justify-center'
  const iconSize = 'h-[24px] w-[24px]'

  switch (object.type) {
    case ActivityObjectType.Message:
      return (
        <div className={iconClass}>
          <Icon icon="RiMessage3Fill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.Checklist:
      return (
        <div className={iconClass}>
          <Icon icon="RiCheckboxMultipleFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.Calculation:
      return (
        <div className={iconClass}>
          <Icon icon="RiCalculatorFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.ClinicalNote:
      return (
        <div className={iconClass}>
          <Icon icon="RiHealthBookFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.Form:
      if (isHITL(activity)) {
        return (
          <div className={iconClass}>
            <Icon icon="RiShieldUserLine" className="h-6 w-6 fill-green-800" />
          </div>
        )
      }
      return (
        <div className={iconClass}>
          <Icon icon="RiSurveyFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.PluginAction:
      if (indirect_object?.name?.toLocaleLowerCase() === 'sendgrid') {
        return (
          <div className={iconClass}>
            <Sendgrid className={`${iconSize} fill-slate-800`} />
          </div>
        )
      }
      return (
        <div className={iconClass}>
          <img
            className={`${iconSize} rounded-md border border-slate-200 fill-slate-800`}
            src={icon_url ?? undefined}
          />
        </div>
      )
    case ActivityObjectType.EmrReport:
      return (
        <div className={iconClass}>
          <Icon icon="RiUploadCloudFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.Timer:
      return (
        <div className={iconClass}>
          <Icon icon="RiTimeLine" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.ApiCall:
      return (
        <div className={iconClass}>
          <Icon icon="RiTerminalBoxFill" className={`${iconSize} fill-slate-800`} />
        </div>
      )
    case ActivityObjectType.Agent:
      return (
        <div className={iconClass}>
          <Icon icon="RiSparklingFill" className={`${iconSize} fill-yellow-400`} />
        </div>
      )
    default:
      return (
        <div className={iconClass}>
          <Icon icon="RiQuestionFill" className={`${iconSize} fill-slate-400`} />
        </div>
      )
  }
}
